import React, { useRef, useState } from 'react';

/**
 * Base component for a touchable element that ensures interactions on both mobile and desktop devices
 * trigger `onClick` only when the touch or mouse click ends within the element.
 *
 * Benefits:
 * - Users can safely be touching another part of the screen and the touch/click will be handled properly.
 * - Prevents `onClick` from triggering if the element was long-pressed (≥2 seconds).
 * - Prevents `onClick` from triggering if the user scrolled or moved significantly after pressing.
 * - Users can decide not to trigger the element by sliding/dragging away from the element before releasing.
 *
 * @param {Object} props - Component props
 * @param {Function} props.onClick - (Required) The function to execute when the element is tapped or clicked
 * @param {React.ElementType} props.component - (Required) The HTML element type (e.g., 'div', 'button', 'span')
 * @param {React.ReactNode} props.children - (Required) The child elements to be wrapped
 * @param {Object} [props.style] - Optional inline styles for the component
 * @param {string} [props.className] - Optional className for styling
 * @returns {JSX.Element} An interactive element with proper touch handling
 */
const Touchable = ({
  component: Component,
  onClick,
  children,
  style,
  className,
  ...props
}) => {
  const elementRef = useRef(null);
  const [isTouchEvent, setIsTouchEvent] = useState(false);
  const interactionStartRef = useRef({});
  const LONG_PRESS_THRESHOLD = 2000; // 2 seconds
  const MOVE_THRESHOLD = 10; // 10 pixels

  /**
   * Extracts client coordinates from either touch or mouse events.
   *
   * For touch events, this function handles an important distinction:
   * - For touch start events: Uses event.touches which contains all current touch points
   * - For touch end events: Uses event.changedTouches which contains the touch points
   *   that triggered the current event
   *
   * @param {Event} event - The touch or mouse event
   * @param {boolean} isEnd - Whether this is an end event (uses changedTouches instead of touches)
   * @returns {Object} - The x and y coordinates
   */
  const getClientCoordinates = (event, isEnd = false) => {
    if (event.type.startsWith('touch')) {
      const touchList = isEnd ? event.changedTouches : event.touches;
      return { clientX: touchList[0].clientX, clientY: touchList[0].clientY };
    }
    return { clientX: event.clientX, clientY: event.clientY };
  };

  /**
   * Checks if the event occurred within the element's boundaries.
   * @param {Event} event - The touch or mouse event
   * @returns {boolean} - Whether the event was within the element's boundaries
   */
  const isEventInsideElement = (event) => {
    if (!elementRef.current) return false;

    const {
      left, top, right, bottom
    } = elementRef.current.getBoundingClientRect();
    const { clientX, clientY } = getClientCoordinates(event, true);

    return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
  };

  /**
   * Records the start of an interaction if it begins inside the element.
   * This allows us to track only interactions that start in our element.
   */
  const handleInteractionStart = (event) => {
    // Prevent tracking mouse events if we recently had a touch event
    if (event.type === 'mousedown' && isTouchEvent) {
      return;
    }

    if (!isEventInsideElement(event)) return;

    const { clientX, clientY } = getClientCoordinates(event);

    // For touch events, store using the touch identifier
    // For mouse events, just use 'mouse' as the key
    const interactionId = event.type.startsWith('touch')
      ? event.touches[0].identifier : 'mouse';

    interactionStartRef.current[interactionId] = {
      x: clientX,
      y: clientY,
      time: Date.now(),
      eventType: event.type
    };
  };

  /**
   * Validates if an interaction meets the criteria for a valid click/tap.
   * For interactions that started within our element, this applies long-press and movement
   * validation. For other interactions, it only checks if the end event is inside the element.
   *
   * @param {Event} event - The touch or mouse event
   * @param {string} eventType - The type of the event ('touch' or 'mouse')
   * @returns {boolean} - Whether the interaction is valid
   */
  const isValidInteraction = (event, eventType) => {
    if (!onClick || !isEventInsideElement(event)) return false;

    // Determine the interaction ID based on event type
    const interactionId = eventType === 'touch'
      ? event.changedTouches[0].identifier : 'mouse';

    // We have no start record for this specific interaction
    if (!interactionStartRef.current[interactionId]) {
      // For touch events without a start record, we still consider them valid
      // This enables multi-touch scenarios where the user might be touching elsewhere
      // However, for mouse events without a start record, we reject them
      // This helps avoid duplicate firing on touch devices that emit mouse events
      return eventType === 'touch';
    }

    const start = interactionStartRef.current[interactionId];
    const { clientX, clientY } = getClientCoordinates(event, true);
    const timeElapsed = Date.now() - start.time;
    const moved = Math.abs(clientX - start.x) > MOVE_THRESHOLD
                 || Math.abs(clientY - start.y) > MOVE_THRESHOLD;

    // Clean up the reference to prevent memory leaks and stale data
    delete interactionStartRef.current[interactionId];

    // Reject long presses
    if (timeElapsed >= LONG_PRESS_THRESHOLD) {
      return false;
    }

    // Reject large movements
    if (moved) {
      return false;
    }

    return true;
  };

  /**
   * Handles the touch end event and ensures it meets criteria for a valid interaction.
   */
  const handleTouchEnd = (event) => {
    // Set the touch event flag to prevent mouseUp from firing
    setIsTouchEvent(true);

    if (isValidInteraction(event, 'touch')) {
      onClick(event);
    }
  };

  /**
   * Handles the mouse up event, checking the touch event flag to prevent duplicate execution.
   */
  const handleMouseUp = (event) => {
    // If we recently had a touch event, ignore this mouse event completely
    // This prevents duplicate firing on devices that emit both events
    if (isTouchEvent) {
      setIsTouchEvent(false); // Reset the flag after a short delay
      setTimeout(() => setIsTouchEvent(false), 300);
      return;
    }

    if (isValidInteraction(event, 'mouse')) {
      onClick(event);
    }
  };

  return (
    <Component
      ref={elementRef}
      onTouchStart={handleInteractionStart}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleInteractionStart}
      onMouseUp={handleMouseUp}
      style={style}
      className={className}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Touchable;
